import {
  AtRiskDeal,
  AtRiskCompany,
  AtRiskFactor,
  StaffingConstraints,
  OverallocatedStaff,
  CapacityWarning,
  UnassignedWork,
  Deal,
} from "@/lib/types/database/deals";
import { ACTIVE_STAGES, hasDealStage } from "@/lib/constants/deal-stages";
import { validateEstimatedHours } from "@/lib/utils/crm/deal-data-validation";
import type { Project_Health_Status, Ticket_Priority } from "@/src/gql/graphql";
import { isCalendarDateOverdue } from "@west-tennessee-consulting/business-date";

const STALLED_DAYS_THRESHOLD = 14;
const LOW_PROBABILITY_THRESHOLD = 30;
const CAPACITY_WARNING_THRESHOLD = 80;

export interface RiskCompanyInput {
  id: string;
  name?: string | null;
  schedule_status?: Project_Health_Status | null;
  budget_status?: Project_Health_Status | null;
  scope_status?: Project_Health_Status | null;
  alignment_status?: Project_Health_Status | null;
}

export interface RiskUserInput {
  id: string;
  email: string;
  first_name?: string | null;
  last_name?: string | null;
  available_hours?: number | null;
  is_billable?: boolean | null;
}

export interface RiskTicketInput {
  id: string;
  title: string;
  priority?: Ticket_Priority | null;
  created_at?: string | null;
  assigned_to?: string | null;
}

export function calculateAtRiskDeals(deals: Deal[]): AtRiskDeal[] {
  const now = new Date();
  return deals
    // `deals.stage` is nullable — a stage-less deal is in no bucket (#2633).
    .filter((deal) => hasDealStage(deal.stage) && ACTIVE_STAGES.includes(deal.stage))
    .map((deal) => {
      const lastActivity = deal.last_activity_at
        ? new Date(deal.last_activity_at)
        : new Date(deal.created_at);
      const daysSinceActivity = Math.floor(
        (now.getTime() - lastActivity.getTime()) / (1000 * 60 * 60 * 24)
      );

      const riskFactors: AtRiskFactor = {
        isStalled: daysSinceActivity >= STALLED_DAYS_THRESHOLD,
        isLowProbability: (deal.close_probability || 50) < LOW_PROBABILITY_THRESHOLD,
        // A deal closing TODAY is not overdue; `new Date(close_date)` is UTC midnight.
        isOverdue: isCalendarDateOverdue(deal.close_date),
      };

      let riskScore = 0;
      if (riskFactors.isStalled) riskScore += 40;
      if (riskFactors.isLowProbability) riskScore += 35;
      if (riskFactors.isOverdue) riskScore += 25;

      return { ...deal, riskFactors, riskScore, daysSinceActivity };
    })
    .filter((deal) => deal.riskScore > 0)
    .sort((a, b) => b.riskScore - a.riskScore);
  // Returns the FULL at-risk set (sorted). Callers showing a preview slice to
  // the top N themselves and label it "Top N of {length}" so the headline count
  // is the true total, never a silently-capped 5.
}

export function calculateAtRiskCompanies(
  atRiskDeals: AtRiskDeal[],
  companies: RiskCompanyInput[]
): AtRiskCompany[] {
  const atRiskCompanyMap = new Map<string, AtRiskCompany>();

  companies.forEach((company) => {
    const hasRedStatus = [
      company.schedule_status,
      company.budget_status,
      company.scope_status,
      company.alignment_status,
    ].includes("red");

    const companyAtRiskDeals = atRiskDeals.filter(
      (deal) => deal.company_id === company.id
    );

    if (hasRedStatus || companyAtRiskDeals.length > 0) {
      const overallStatus = hasRedStatus
        ? "red"
        : companyAtRiskDeals.length > 0
          ? "yellow"
          : "green";

      atRiskCompanyMap.set(company.id, {
        company_id: company.id,
        company_name: company.name || null,
        company_name_missing: !company.name,
        projectHealthStatus: overallStatus,
        atRiskDealCount: companyAtRiskDeals.length,
        topRiskDeal: companyAtRiskDeals[0],
        riskScore:
          companyAtRiskDeals.length > 0
            ? companyAtRiskDeals[0].riskScore
            : hasRedStatus
              ? 50
              : 0,
      });
    }
  });

  atRiskDeals.forEach((deal) => {
    if (!atRiskCompanyMap.has(deal.company_id)) {
      const companyAtRiskDeals = atRiskDeals.filter(
        (d) => d.company_id === deal.company_id
      );

      atRiskCompanyMap.set(deal.company_id, {
        company_id: deal.company_id,
        company_name: null,
        company_name_missing: true,
        projectHealthStatus: "yellow",
        atRiskDealCount: companyAtRiskDeals.length,
        topRiskDeal: companyAtRiskDeals[0],
        riskScore: companyAtRiskDeals[0].riskScore,
      });
    }
  });

  return Array.from(atRiskCompanyMap.values()).sort((a, b) => b.riskScore - a.riskScore);
  // Full at-risk company set (sorted). Preview surfaces slice to the top N.
}

export function calculateStaffingConstraints(
  users: RiskUserInput[],
  deals: Deal[],
  tickets: RiskTicketInput[]
): StaffingConstraints {
  const overallocatedStaff: OverallocatedStaff[] = [];
  const capacityWarnings: CapacityWarning[] = [];

  users.forEach((user) => {
    if (!user.is_billable) return;

    const availableHours =
      user.available_hours !== null && user.available_hours !== undefined
        ? user.available_hours
        : 40;

    if (availableHours <= 0) return;

    const userDeals = deals.filter((deal) => {
      if (!deal.assigned_users || !Array.isArray(deal.assigned_users)) return false;
      return deal.assigned_users.includes(user.id);
    });

    const validatedHours = userDeals
      .map((deal) => {
        const { isValid, value } = validateEstimatedHours(deal.estimated_hours);
        return isValid && value !== null && value !== undefined && value > 0 ? value : 0;
      })
      .filter((hours) => hours > 0);

    const totalEstimatedHours = validatedHours.reduce((sum, hours) => sum + hours, 0);

    const utilizationPercentage = (totalEstimatedHours / availableHours) * 100;

    if (utilizationPercentage > 100) {
      overallocatedStaff.push({
        user_id: user.id,
        user_name: `${user.first_name || ""} ${user.last_name || ""}`.trim(),
        email: user.email,
        available_hours: availableHours,
        allocated_hours: totalEstimatedHours,
        overallocation_percentage: Math.round(utilizationPercentage - 100),
      });
    }

    if (utilizationPercentage > CAPACITY_WARNING_THRESHOLD && utilizationPercentage <= 100) {
      capacityWarnings.push({
        user_id: user.id,
        user_name: `${user.first_name || ""} ${user.last_name || ""}`.trim(),
        email: user.email,
        utilization_percentage: Math.round(utilizationPercentage),
        available_hours: availableHours,
        hours_worked: totalEstimatedHours,
      });
    }
  });

  const unassignedWork: UnassignedWork[] = tickets
    .filter((ticket) => !ticket.assigned_to)
    .map((ticket) => {
      const createdDate = ticket.created_at ? new Date(ticket.created_at) : new Date();
      const now = new Date();
      const daysUnassigned = Math.floor(
        (now.getTime() - createdDate.getTime()) / (1000 * 60 * 60 * 24)
      );
      return {
        ticket_id: ticket.id,
        ticket_title: ticket.title,
        priority: ticket.priority ?? "",
        created_at: ticket.created_at ?? "",
        days_unassigned: daysUnassigned,
      };
    })
    .sort((a, b) => b.days_unassigned - a.days_unassigned)
    .slice(0, 10);

  return { overallocatedStaff, capacityWarnings, unassignedWork };
}
